import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';

import { Button, EmptyState, EnterprisePowerUpAdmin, Skeleton } from '@/components';
import { announce } from '@/lib/announce';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  useBulkDisableEnterprisePowerUp,
  useEnterprisePowerUpAffectedBoards,
  useEnterprisePowerUpPolicy,
  useEnterprisePowerUpUsage,
  useUpdateEnterprisePowerUpPolicy,
  usePowerUpDirectory,
} from '@/lib/power-ups';
import { signInHref, useSession } from '@/lib/session';

export const Route = createFileRoute('/enterprises/$enterpriseId/power-ups')({
  component: EnterprisePowerUpsRoute,
});

/**
 * ⚠ A CHOICE OF KEY, replacing `Enterprise Power-Up ${postureLabel(posture)}
 * policy saved.` — the deleted helper dropped a lower-cased English noun into
 * the middle of a live-region sentence. The record KEYS stay the WIRE values;
 * only the sentence is chrome.
 */
const POLICY_SAVED_KEYS: Record<'allow_list' | 'block_list', string> = {
  allow_list: 'powerUps.route.allowListPolicySaved',
  block_list: 'powerUps.route.blockListPolicySaved',
};

function PageFrame({ children }: { readonly children: ReactElement }): ReactElement {
  const { t } = useInterfaceLanguage();

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-1">
        <h1 className="font-display text-3xl font-semibold text-ink">
          {t('powerUps.route.title')}
        </h1>
        <p className="max-w-3xl text-sm text-ink-muted">
          {t('powerUps.route.description')}
        </p>
      </header>
      {children}
    </div>
  );
}

function EnterprisePowerUpsRoute(): ReactElement {
  const { t } = useInterfaceLanguage();
  const { enterpriseId } = Route.useParams();
  const location = useLocation();
  const { profile, isPending, isSignedOut, error } = useSession();
  const enabled = !isSignedOut && !isPending && profile !== null;
  const [selectedAffectedPowerUpId, setSelectedAffectedPowerUpId] = useState<
    string | null
  >(null);

  const directory = usePowerUpDirectory({}, enabled);
  const policy = useEnterprisePowerUpPolicy(enterpriseId, enabled);
  const usage = useEnterprisePowerUpUsage(enterpriseId, enabled);
  const affectedBoards = useEnterprisePowerUpAffectedBoards(
    enterpriseId,
    selectedAffectedPowerUpId,
  );
  const updatePolicy = useUpdateEnterprisePowerUpPolicy(enterpriseId);
  const bulkDisable = useBulkDisableEnterprisePowerUp(enterpriseId);

  if (isSignedOut) {
    return (
      <PageFrame>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('powerUps.route.signInTitle')}
          body={t('powerUps.route.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              href={signInHref({
                pathname: location.pathname,
                search: location.searchStr,
              })}
            >
              {t('powerUps.route.signInAction')}
            </Button>
          }
        />
      </PageFrame>
    );
  }

  if (isPending) {
    return (
      <PageFrame>
        <div aria-busy="true" className="flex flex-col gap-3">
          <Skeleton shape="block" count={3} />
        </div>
      </PageFrame>
    );
  }

  if (profile === null) {
    return (
      <PageFrame>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('powerUps.route.accountErrorTitle')}
          body={error?.message ?? t('powerUps.route.genericError')}
        />
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <EnterprisePowerUpAdmin
        directoryEntries={directory.data?.powerUps ?? []}
        policy={policy.data}
        usage={usage.data}
        affectedBoards={affectedBoards.data}
        loading={policy.isPending}
        directoryLoading={directory.isPending}
        usageLoading={usage.isPending}
        affectedBoardsLoading={affectedBoards.isPending}
        error={policy.error?.message}
        directoryError={directory.error?.message}
        usageError={usage.error?.message}
        affectedBoardsError={affectedBoards.error?.message}
        policyBusy={updatePolicy.isPending}
        bulkBusyPowerUpId={
          bulkDisable.isPending ? (bulkDisable.variables?.powerUpId ?? null) : null
        }
        selectedAffectedPowerUpId={selectedAffectedPowerUpId}
        onSelectAffectedPowerUp={setSelectedAffectedPowerUpId}
        onSavePolicy={(posture, powerUpIds) =>
          updatePolicy.mutate(
            { posture, powerUpIds },
            {
              onSuccess: () => {
                announce(t(POLICY_SAVED_KEYS[posture]));
              },
            },
          )
        }
        onBulkDisable={(powerUpId, keepData) =>
          bulkDisable.mutate(
            { powerUpId, keepData },
            {
              onSuccess: (result) => {
                /* ⚠ PLURAL, and the THIRD argument is load-bearing:
                   `interfaceMessage` selects `forms.other` whenever
                   `count !== 1`, so omitting it announces `1 Power-Up installs
                   disabled.` to a screen reader and to nothing else. */
                const count = result.disabledBoardPowerUpIds.length;
                announce(t('powerUps.route.installsDisabled', { count }, count));
              },
            },
          )
        }
        onRetryPolicy={() => void policy.refetch()}
        onRetryDirectory={() => void directory.refetch()}
        onRetryUsage={() => void usage.refetch()}
        onRetryAffectedBoards={() => void affectedBoards.refetch()}
      />
    </PageFrame>
  );
}
